const STORAGE_KEY = 'stormsurge-weekly-v1';
const COMPETITOR_ADJECTIVES = [
  'Shadow',
  'Toxic',
  'Swift',
  'Silent',
  'Savage',
  'Neon',
  'Wild',
  'Arctic',
  'Crimson',
  'Frozen',
  'Storm',
  'Vivid',
  'Rogue',
  'Void',
  'Hyper',
] as const;
const COMPETITOR_NOUNS = [
  'Raptor',
  'Reaper',
  'Viper',
  'Hawk',
  'Wolf',
  'Sniper',
  'Raider',
  'Knight',
  'Fox',
  'Drift',
  'Bullet',
  'Spark',
  'Edge',
  'Fang',
  'Titan',
] as const;
export const WEEKLY_WIN_MILESTONE = 5;
const PLAYER_NAME = 'You';
const COMPETITOR_COUNT = 9;

export type WeeklyPlayerStats = {
  wins: number;
  kills: number;
  matches: number;
};

export type WeeklyLeaderboardEntry = {
  name: string;
  wins: number;
  kills: number;
  matches: number;
  isPlayer?: boolean;
};

export type WeeklyLeaderboard = {
  top: WeeklyLeaderboardEntry;
  playerRank: number;
  entries: WeeklyLeaderboardEntry[];
};

export type WeeklyMatchResult = {
  stats: WeeklyPlayerStats;
  milestoneHit: boolean;
  playerRank: number;
};

type PersistedWeekly = {
  weekKey: string;
  stats: WeeklyPlayerStats;
};

function defaultStats(): WeeklyPlayerStats {
  return { wins: 0, kills: 0, matches: 0 };
}

/** ISO week key, e.g. `2026-W21` (Monday-based, UTC). */
export function getWeekKey(d = new Date()): string {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function hashWeekSeed(weekKey: string): number {
  let h = 2166136261;
  for (let i = 0; i < weekKey.length; i++) {
    h ^= weekKey.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function loadState(): PersistedWeekly {
  const weekKey = getWeekKey();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { weekKey, stats: defaultStats() };
    const parsed = JSON.parse(raw) as PersistedWeekly;
    if (parsed.weekKey !== weekKey) return { weekKey, stats: defaultStats() };
    const s = parsed.stats ?? defaultStats();
    return {
      weekKey,
      stats: {
        wins: Number.isFinite(s.wins) && s.wins >= 0 ? Math.floor(s.wins) : 0,
        kills: Number.isFinite(s.kills) && s.kills >= 0 ? Math.floor(s.kills) : 0,
        matches: Number.isFinite(s.matches) && s.matches >= 0 ? Math.floor(s.matches) : 0,
      },
    };
  } catch {
    return { weekKey, stats: defaultStats() };
  }
}

function saveState(state: PersistedWeekly): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

function compareEntries(a: WeeklyLeaderboardEntry, b: WeeklyLeaderboardEntry): number {
  if (b.wins !== a.wins) return b.wins - a.wins;
  if (b.kills !== a.kills) return b.kills - a.kills;
  if (b.matches !== a.matches) return b.matches - a.matches;
  if (a.isPlayer) return -1;
  if (b.isPlayer) return 1;
  return a.name.localeCompare(b.name);
}

function competitorName(rand: () => number, used: Set<string>): string {
  for (let guard = 0; guard < 24; guard++) {
    const adj = COMPETITOR_ADJECTIVES[Math.floor(rand() * COMPETITOR_ADJECTIVES.length)]!;
    const noun = COMPETITOR_NOUNS[Math.floor(rand() * COMPETITOR_NOUNS.length)]!;
    const digits = rand() < 0.5 ? 2 : 3;
    const max = 10 ** digits;
    const n = Math.floor(rand() * max)
      .toString()
      .padStart(digits, '0');
    const name = `${adj}${noun}${n}`;
    if (!used.has(name)) return name;
  }
  return `CupRival${Math.floor(rand() * 900 + 100)}`;
}

function generateCompetitors(weekKey: string): WeeklyLeaderboardEntry[] {
  const rand = mulberry32(hashWeekSeed(weekKey));
  const used = new Set<string>();
  const entries: WeeklyLeaderboardEntry[] = [];
  for (let i = 0; i < COMPETITOR_COUNT; i++) {
    const name = competitorName(rand, used);
    used.add(name);
    const wins = Math.floor(rand() * 6);
    const kills = Math.floor(rand() * 28) + wins * 3;
    const matches = wins + Math.floor(rand() * 8) + 2;
    entries.push({ name, wins, kills, matches });
  }
  return entries;
}

/** Persistent weekly cup stats — resets automatically on a new ISO week. */
export class FpsWeeklyBoard {
  static loadStats(): WeeklyPlayerStats {
    return { ...loadState().stats };
  }

  static recordMatch(opts: { won: boolean; kills: number }): WeeklyMatchResult {
    const state = loadState();
    const prevWins = state.stats.wins;
    state.stats.matches += 1;
    state.stats.kills += Math.max(0, Math.floor(opts.kills));
    if (opts.won) state.stats.wins += 1;
    saveState(state);
    const board = this.getLeaderboard();
    return {
      stats: { ...state.stats },
      milestoneHit: opts.won && prevWins < WEEKLY_WIN_MILESTONE && state.stats.wins >= WEEKLY_WIN_MILESTONE,
      playerRank: board.playerRank,
    };
  }

  static getLeaderboard(): WeeklyLeaderboard {
    const { weekKey, stats } = loadState();
    const player: WeeklyLeaderboardEntry = {
      name: PLAYER_NAME,
      wins: stats.wins,
      kills: stats.kills,
      matches: stats.matches,
      isPlayer: true,
    };
    const entries = [...generateCompetitors(weekKey), player].sort(compareEntries);
    const playerRank = entries.findIndex((e) => e.isPlayer) + 1;
    return {
      top: entries[0]!,
      playerRank,
      entries,
    };
  }

  /** Lobby panel HTML — weekly wins/kills and progress toward the 5-win milestone. */
  static getLobbySummary(): string {
    const stats = this.loadStats();
    const board = this.getLeaderboard();
    const winPct = Math.min(100, Math.round((stats.wins / WEEKLY_WIN_MILESTONE) * 100));
    const milestoneDone = stats.wins >= WEEKLY_WIN_MILESTONE;
    const barColor = milestoneDone ? '#ffd54a' : '#a78bfa';
    const top = board.top;
    const topLabel = top.isPlayer ? 'You lead the cup!' : `${top.name} · ${top.wins}W`;
    return (
      '<div id="lobby-weekly-cup" style="margin-top:10px;padding:10px 12px;border-radius:10px;' +
      'background:rgba(0,0,0,0.28);border:1px solid rgba(167,139,250,0.45);text-align:left">' +
      '<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px">' +
      '<div style="font:800 10px system-ui;color:#c4b5fd;letter-spacing:0.12em;text-transform:uppercase">Weekly Cup</div>' +
      `<div style="font:700 9px system-ui;color:#a5b4fc;letter-spacing:0.04em;white-space:nowrap">Rank #${board.playerRank}</div>` +
      '</div>' +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px">' +
      `<div style="padding:8px;border-radius:8px;background:rgba(255,255,255,0.06)"><div style="font:800 18px system-ui;color:#ffe066">${stats.wins}</div>` +
      '<div style="font:700 9px system-ui;color:#8899aa;margin-top:2px;letter-spacing:0.06em">WINS</div></div>' +
      `<div style="padding:8px;border-radius:8px;background:rgba(255,255,255,0.06)"><div style="font:800 18px system-ui;color:#ff8a8a">${stats.kills}</div>` +
      '<div style="font:700 9px system-ui;color:#8899aa;margin-top:2px;letter-spacing:0.06em">KILLS</div></div>' +
      '</div>' +
      '<div style="display:flex;align-items:center;justify-content:space-between;gap:6px;margin-bottom:4px">' +
      `<span style="font:600 10px system-ui;color:#b8c4ff">${milestoneDone ? 'Cup milestone complete!' : `${stats.wins}/${WEEKLY_WIN_MILESTONE} wins to qualify`}</span>` +
      `<span style="font:700 9px system-ui;color:${milestoneDone ? '#ffd54a' : '#9ca3af'}">${milestoneDone ? 'Done' : `${winPct}%`}</span>` +
      '</div>' +
      '<div style="height:6px;border-radius:3px;background:rgba(255,255,255,0.1);overflow:hidden">' +
      `<div style="height:100%;width:${winPct}%;background:${barColor};transition:width 0.25s ease-out"></div>` +
      '</div>' +
      `<div style="margin-top:7px;font:600 9px system-ui;color:#8b9cb8;line-height:1.35">Leader: ${topLabel}</div>` +
      '</div>'
    );
  }
}
